import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { supabase } from '../supabase';

const getCurrency = (lang) => lang==='en'?'$':lang==='da'?'kr (DKK)':lang==='sv'?'kr (SEK)':'kr';
const fmt = (n, lang) => {
  const locale = lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO';
  const num = Math.round(Math.abs(n)).toLocaleString(locale);
  if (lang==='en') return '$' + num;
  if (lang==='sv') return num + ' kr';
  if (lang==='da') return num + ' kr';
  return num + ' kr';
};
const DAILY_LIMIT = 20;
const RATE_LIMIT = 5; // meldinger per minutt

const getSuggestions = (lang) => [
  lang==='en'?'Can I afford a new phone?':lang==='sv'?'Har jag råd med en ny telefon?':lang==='da'?'Har jeg råd til en ny telefon?':'Har jeg råd til en ny telefon?',
  lang==='en'?'How can I save faster?':lang==='sv'?'Hur kan jag spara snabbare?':lang==='da'?'Hvordan kan jeg spare hurtigere?':'Hvordan kan jeg spare raskere?',
  lang==='en'?'Give me a budget for next week':lang==='sv'?'Ge mig en budget för nästa vecka':lang==='da'?'Giv mig et budget for næste uge':'Gi meg et budsjett for neste uke',
  lang==='en'?'What is BSU and should I have it?':lang==='sv'?'Vad är BSU och bör jag ha det?':lang==='da'?'Hvad er BSU og bør jeg have det?':'Hva er BSU og bør jeg ha det?',
];

export default function TabChat() {
  const { state, dispatch } = useApp();
  const lang = state.language || 'en';
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msgCount, setMsgCount] = React.useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('savely_chat_count') || '{}');
      const today = new Date().toDateString();
      return stored.date === today ? (stored.count || 0) : 0;
    } catch(e) { return 0; }
  });
  const updateMsgCount = (n) => {
    setMsgCount(n);
    try { localStorage.setItem('savely_chat_count', JSON.stringify({ date: new Date().toDateString(), count: n })); } catch(e) {}
  };
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [recentMsgTimes, setRecentMsgTimes] = useState([]);
  const msgsRef = useRef(null);

  const isAdvanced = state.plan === 'advanced';
  const isPremium = state.plan === 'premium';
  const hasAccess = isAdvanced || isPremium;
  const atLimit = isAdvanced && msgCount >= DAILY_LIMIT;
  const remaining = DAILY_LIMIT - msgCount;

  useEffect(() => {
    if (hasAccess) {
      setMessages([{ role:'assistant', text:
        lang==='en'?`Hi ${state.user?.username || state.user?.name || ''}! 👋 I'm your personal finance coach. How can I help you save more today? 💰`
        :lang==='sv'?`Hej ${state.user?.username || state.user?.name || ''}! 👋 Jag är din personliga ekonomicoach. Hur kan jag hjälpa dig spara mer idag? 💰`
        :lang==='da'?`Hej ${state.user?.username || state.user?.name || ''}! 👋 Jeg er din personlige økonomicoach. Hvordan kan jeg hjælpe dig med at spare mere i dag? 💰`
        :`Hei ${state.user?.username || state.user?.name || ''}! 👋 Jeg er din personlige økonomicoach. Hvordan kan jeg hjelpe deg spare mer i dag? 💰`
      }]);
    }
  }, [hasAccess]);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages]);

  const income = state.income || 22000;
  const spent = (state.transactions || []).filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading || !hasAccess || atLimit) return;

    // Rate limit check — maks 5 meldinger per minutt
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    const recentCount = recentMsgTimes.filter(t => t > oneMinuteAgo).length;
    if (recentCount >= RATE_LIMIT) {
      setRateLimited(true);
      setTimeout(() => setRateLimited(false), 10000);
      return;
    }
    setRecentMsgTimes(prev => [...prev.filter(t => t > oneMinuteAgo), now]);

    setInput('');
    setMessages(prev => [...prev, { role:'user', text:msg }]);
    const newHist = [...history, { role:'user', content:msg }];
    setHistory(newHist);
    if (isAdvanced) updateMsgCount(msgCount + 1);
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/chat', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({
          userId: session?.user?.id,
          messages: newHist,
          userContext: { lang: lang,
            name: state.user?.name,
            income: fmt(income, lang),
            balance: fmt(state.balance, lang),
            dailyBudget: fmt(state.dailyBudget, lang),
            dailySpent: fmt(state.dailySpent, lang),
            goals: (state.goals || []).map(g=>g.name).join(', ')||t(lang,'none') || 'None',
            spent: fmt(spent, lang),
            plan: isPremium ? 'Premium' : 'Advanced',
          }
        }),
      });
      const data = await res.json();
      if (data.error) {
        setMessages(prev => [...prev, { role:'assistant', text: data.error }]);
        setLoading(false);
        return;
      }
      const reply = data.content || lang==='en'?'Sorry, try again!':lang==='sv'?'Förlåt, försök igen!':lang==='da'?'Beklager, prøv igen!':'Beklager, prøv igjen!';
      setHistory(prev => [...prev, { role:'assistant', content:reply }]);
      setMessages(prev => [...prev, { role:'assistant', text:reply }]);
    } catch {
      setMessages(prev => [...prev, { role:'assistant', text:lang==='en'?'Could not connect. Try again.':lang==='sv'?'Kunde inte ansluta. Försök igen.':lang==='da'?'Kunne ikke oprette forbindelse. Prøv igen.':'Klarte ikke å koble til. Prøv igjen.' }]);
    }
    setLoading(false);
  };

  // Ingen tilgang — vis upsell
  if (!hasAccess) {
    return (
      <div style={{ padding:'1.25rem 1rem', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'70vh', textAlign:'center' }}>
        <div style={{ fontSize:56, marginBottom:'1rem' }}>🤖</div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, color:'var(--ink)', marginBottom:8 }}>{lang==='en'?'AI Coach':lang==='sv'?'AI-coach':lang==='da'?'AI-coach':'AI-coach'}</div>
        <div style={{ fontSize:14, color:'var(--ink3)', lineHeight:1.6, marginBottom:'2rem', maxWidth:280 }}>
          {lang==='en'?'Get a personal finance coach that helps you save smarter and gives you concrete advice.':lang==='sv'?'Få en personlig ekonomicoach som hjälper dig spara smartare och ger dig konkreta råd.':lang==='da'?'Få en personlig økonomicoach der hjælper dig spare smartere og giver dig konkrete råd.':'Få en personlig økonomicoach som hjelper deg spare smartere og gir deg konkrete råd.'}
        </div>
        <div style={{ width:'100%', display:'flex', flexDirection:'column', gap:12, marginBottom:'1.5rem' }}>
          <div style={{ background:'linear-gradient(135deg,rgba(124,58,237,0.12),rgba(124,58,237,0.04))', border:'2px solid #7C3AED', borderRadius:14, padding:'1rem', position:'relative' }}>
            <div style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', background:'#7C3AED', color:'#fff', fontSize:10, fontWeight:800, borderRadius:999, padding:'3px 12px', whiteSpace:'nowrap' }}>
              {lang==='en'?'BEST VALUE':lang==='sv'?'BÄST VÄRDE':lang==='da'?'BEDST VÄRDI':'BEST VERDI'}
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, color:'#5B21B6' }}>
                👑 Premium
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:'#5B21B6' }}>{lang==='en'?'$15/mo':lang==='sv'?'149 kr/mån':lang==='da'?'149 kr/mdr':'149 kr/mnd'}</div>
            </div>
            <div style={{ fontSize:12, color:'#6D28D9', marginBottom:4 }}>{lang==='en'?'✓ Unlimited AI Finance Coach':lang==='sv'?'✓ Obegränsad AI-coach':lang==='da'?'✓ Ubegrænset AI-coach':'✓ Ubegrenset AI-økonomicoach'}</div>
            <div style={{ fontSize:12, color:'#6D28D9', marginBottom:4 }}>{lang==='en'?'✓ Daily personal challenges':lang==='sv'?'✓ Dagliga utmaningar':lang==='da'?'✓ Daglige udfordringer':'✓ Daglige personlige utfordringer'}</div>
            <div style={{ fontSize:12, color:'#6D28D9', marginBottom:10 }}>{lang==='en'?'✓ Premium themes + 15 achievements':lang==='sv'?'✓ Premium-teman + 15 prestationer':lang==='da'?'✓ Premium-temaer + 15 præstationer':'✓ Premium-temaer + 15 prestasjoner'}</div>
            <button onClick={() => dispatch({ type:'SET_PLAN', payload:'premium' })} style={{ width:'100%', padding:'12px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#7C3AED,#A855F7)', color:'#fff', fontWeight:800, cursor:'pointer', fontSize:14, boxShadow:'0 4px 15px rgba(124,58,237,0.4)' }}>
              {lang==='en'?'👑 Get Premium →':lang==='sv'?'👑 Välj Premium →':lang==='da'?'👑 Vælg Premium →':'👑 Velg Premium →'}
            </button>
          </div>
        </div>
        <div style={{ fontSize:11, color:'var(--ink4)' }}>{lang==='en'?'Cancel anytime. No commitment.':lang==='sv'?'Avsluta när som helst. Ingen bindningstid.':lang==='da'?'Annuller når som helst. Ingen binding.':'Avbryt når som helst. Ingen bindingstid.'}</div>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100dvh - 80px)', padding:'1.25rem 1rem 0' }}>

      {/* Oppgrader modal */}
      {showUpgrade && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:999 }}>
          <div style={{ background:'var(--card)', borderRadius:'24px 24px 0 0', padding:'1.5rem', width:'100%', maxWidth:430 }}>
            <div style={{ width:40, height:4, background:'var(--border2)', borderRadius:999, margin:'0 auto 1.25rem' }}/>
            <div style={{ textAlign:'center', marginBottom:'1.25rem' }}>
              <div style={{ fontSize:36, marginBottom:8 }}>🤖</div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, color:'var(--ink)', marginBottom:6 }}>{t(lang,'unlimitedChat')}</div>
              <div style={{ fontSize:14, color:'var(--ink3)', lineHeight:1.6 }}>Med Premium får du ubegrenset AI-chat, full personlig coach og mye mer.</div>
            </div>
            <button onClick={() => { dispatch({ type:'SET_PLAN', payload:'premium' }); setShowUpgrade(false); }} style={{
              width:'100%', padding:14, background:'#7C3AED', color:'white',
              border:'none', borderRadius:'var(--r-lg)', fontSize:15, fontWeight:700, cursor:'pointer', marginBottom:10,
            }}>{lang==='en'?lang==='en'?'👑 Get Premium — $14.9/mo':lang==='sv'?'👑 Välj Premium — 149 kr/mån':lang==='da'?'👑 Vælg Premium — 149 kr/mdr':'👑 Velg Premium — 149 kr/mnd':lang==='sv'?'🟣 Välj Premium — 149 kr/mån':lang==='da'?'🟣 Vælg Premium — 149 kr/md':'🟣 Velg Premium — 149 kr/mnd'}</button>
            <button onClick={() => setShowUpgrade(false)} style={{ width:'100%', padding:12, background:'none', border:'none', color:'var(--ink3)', fontSize:14, cursor:'pointer' }}>{lang==='en'?'Not now':lang==='sv'?'Inte nu':lang==='da'?'Ikke nu':'Ikke nå'}</button>
          </div>
        </div>
      )}
      <div className="fade-up" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem' }}>
        <div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, color:'var(--ink)' }}>{lang==='en'?'AI Coach':lang==='sv'?'AI-coach':lang==='da'?'AI-coach':'AI-coach'}</div>
          <div style={{ fontSize:12, color:'var(--ink3)' }}>
            {isPremium ? '🟣 Premium — ubegrenset chat' : `🔵 Advanced — ${remaining} av ${DAILY_LIMIT} meldinger igjen i dag`}
          </div>
        </div>
        {!isPremium && (
          <button onClick={() => setShowUpgrade(true)} style={{ background:'#7C3AED', color:'white', border:'none', borderRadius:'var(--r-full)', padding:'7px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
            Ubegrenset 🟣
          </button>
        )}
      </div>

      {/* Grense nådd */}
      {atLimit && (
        <div style={{ background:'#FFF7ED', border:'1.5px solid #FED7AA', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'0.75rem', textAlign:'center' }}>
          <div style={{ fontSize:20, marginBottom:6 }}>⏰</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, color:'#92400E', marginBottom:4 }}>{lang==='en'?'You have used 20 messages today':lang==='sv'?'Du har använt 20 meddelanden idag':lang==='da'?'Du har brugt 20 beskeder i dag':'Du har brukt 20 meldinger i dag'}</div>
          <div style={{ fontSize:12, color:'#78350F', marginBottom:10 }}>Grensen nullstilles i morgen, eller oppgrader til Premium for ubegrenset chat.</div>
          <button onClick={() => dispatch({ type:'SET_PLAN', payload:'premium' })} style={{ padding:'9px 18px', background:'#7C3AED', color:'white', border:'none', borderRadius:'var(--r-full)', fontSize:13, fontWeight:700, cursor:'pointer' }}>
            {lang==='en'?'Upgrade to Premium →':lang==='sv'?'Uppgradera till Premium →':lang==='da'?'Opgrader til Premium →':'Oppgrader til Premium →'}
          </button>
        </div>
      )}

      {/* Rate limit advarsel */}
      {rateLimited && (
        <div style={{ background:'#FFF7ED', border:'1.5px solid #FED7AA', borderRadius:'var(--r-md)', padding:'0.75rem', marginBottom:'0.75rem', textAlign:'center', fontSize:13, color:'#92400E', fontWeight:600 }}>
          {lang==='en'?`⏱ Slow down! Max ${RATE_LIMIT} messages per minute.`:lang==='sv'?`⏱ Sakta ner! Max ${RATE_LIMIT} meddelanden per minut.`:lang==='da'?`⏱ Sænk farten! Maks ${RATE_LIMIT} beskeder per minut.`:`⏱ Sakte ned litt! Maks ${RATE_LIMIT} meldinger per minutt.`}
        </div>
      )}

      {/* Advarsel når nær grensen */}
      {isAdvanced && remaining <= 5 && remaining > 0 && (
        <div style={{ background:'var(--y50)', border:'1px solid var(--y100)', borderRadius:'var(--r-md)', padding:'0.75rem', marginBottom:'0.75rem', fontSize:12, color:'var(--y600)', textAlign:'center' }}>
          ⚠️ {lang==='en'?<>You only have <strong>{remaining} messages</strong> left today</>:lang==='sv'?<>Du har bara <strong>{remaining} meddelanden</strong> kvar idag</>:lang==='da'?<>Du har kun <strong>{remaining} beskeder</strong> tilbage i dag</>:<>Du har kun <strong>{remaining} meldinger</strong> igjen i dag</>}
        </div>
      )}

      {/* Hurtigvalg */}
      <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:8, marginBottom:6, WebkitOverflowScrolling:'touch', scrollbarWidth:'none' }}>
        {getSuggestions(lang).map(s => (
          <button key={s} onClick={() => !atLimit && send(s)} style={{
            flexShrink:0, fontSize:12, padding:'7px 12px',
            background:'var(--card)', border:'1px solid var(--border)',
            borderRadius:'var(--r-full)', color: atLimit ? 'var(--ink4)' : 'var(--ink2)',
            whiteSpace:'nowrap', cursor: atLimit ? 'default' : 'pointer',
          }}>{s}</button>
        ))}
      </div>

      {/* Meldinger */}
      <div ref={msgsRef} style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:12, paddingBottom:12 }}>
        {messages.map((m,i) => (
          <div key={i} style={{ display:'flex', flexDirection:'column', alignItems: m.role==='user' ? 'flex-end' : 'flex-start' }}>
            {m.role==='assistant' && (
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                <div style={{ width:22, height:22, borderRadius:'50%', background:'var(--g400)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12 }}>🤖</div>
                <span style={{ fontSize:10, color:'var(--ink3)', fontWeight:600 }}>Savely AI</span>
              </div>
            )}
            <div style={{
              maxWidth:'82%', padding:'11px 14px', fontSize:14, lineHeight:1.55,
              borderRadius: m.role==='user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              background: m.role==='user' ? 'var(--ink)' : 'var(--card)',
              color: m.role==='user' ? '#fff' : 'var(--ink)',
              border: m.role==='assistant' ? '1px solid var(--border)' : 'none',
            }}>{m.text}</div>
          </div>
        ))}
        {loading && (
          <div style={{ display:'flex', alignItems:'flex-start', gap:6 }}>
            <div style={{ width:22, height:22, borderRadius:'50%', background:'var(--g400)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12 }}>🤖</div>
            <div style={{ padding:'11px 14px', background:'var(--card)', border:'1px solid var(--border)', borderRadius:'18px 18px 18px 4px', fontSize:14, color:'var(--ink3)', fontStyle:'italic' }}>{lang==='en'?'Thinking...':lang==='sv'?'Tänker...':lang==='da'?'Tænker...':'Tenker...'}</div>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ display:'flex', gap:8, padding:'0.75rem 0 1rem', borderTop:'1px solid var(--border)' }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key==='Enter' && send()}
          placeholder={atLimit ? lang==='en'?'Daily limit reached...':lang==='sv'?'Daglig gräns nådd...':lang==='da'?'Daglig grænse nået...':'Daglig grense nådd...' : rateLimited ? 'Vent litt før neste melding...' : lang==='en'?'Ask me anything...':lang==='sv'?'Fråga mig vad som helst...':lang==='da'?'Spørg mig om hvad som helst...':'Spør meg hva som helst...'}
          disabled={atLimit || rateLimited}
          style={{ flex:1, fontSize:14, borderRadius:'var(--r-full)', padding:'10px 16px', opacity: atLimit ? 0.5 : 1 }}
        />
        <button onClick={() => send()} disabled={atLimit} style={{
          width:44, height:44, background: atLimit || rateLimited ? 'var(--surface)' : 'var(--g400)',
          border:'none', borderRadius:'50%', fontSize:18,
          display:'flex', alignItems:'center', justifyContent:'center',
          cursor: atLimit || rateLimited ? 'default' : 'pointer',
          opacity: atLimit || rateLimited ? 0.5 : 1,
        }}>↑</button>
      </div>
    </div>
  );
}
